import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { logoutAdmin } from '../../services/authService';
import { projectsService, blogsService, researchService, involvementsService, awardsService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    projects: 0,
    writings: 0,
    research: 0,
    involvements: 0,
    awards: 0,
    loading: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [projectsResult, blogsResult, researchResult, involvementsResult, awardsResult] = await Promise.all([
        projectsService.getAll(),
        blogsService.getAll(),
        researchService.getAll(),
        involvementsService.getAll(),
        awardsService.getAll()
      ]);

      setStats({
        projects: projectsResult.success ? projectsResult.data.length : 0,
        writings: blogsResult.success ? blogsResult.data.length : 0,
        research: researchResult.success ? researchResult.data.length : 0,
        involvements: involvementsResult.success ? involvementsResult.data.length : 0,
        awards: awardsResult.success ? awardsResult.data.length : 0,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const handleLogout = async () => {
    const result = await logoutAdmin();
    if (result.success) {
      toast.success('Logged out successfully');
      setTimeout(() => {
        navigate('/');
      }, 500);
    }
  };

  const sections = [
    {
      title: 'Profile',
      route: '/adminaccess/profile',
      color: 'pink'
    },
    {
      title: 'Education',
      route: '/adminaccess/education',
      color: 'cyan'
    },
    {
      title: 'Experience',
      route: '/adminaccess/experience',
      color: 'orange'
    },
    {
      title: 'Involvements',
      route: '/adminaccess/involvements',
      color: 'cyan',
      stat: stats.involvements
    },
    {
      title: 'Awards & Recognition',
      route: '/adminaccess/awards',
      color: 'yellow',
      stat: stats.awards
    },
    {
      title: 'Projects',
      route: '/adminaccess/projects',
      color: 'purple',
      stat: stats.projects
    },
    {
      title: 'Research',
      route: '/adminaccess/research',
      color: 'blue',
      stat: stats.research
    },
    {
      title: 'Writings',
      route: '/adminaccess/writings',
      color: 'green',
      stat: stats.writings
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-500/10 border-purple-500/20 hover:bg-purple-500/20',
      blue: 'bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20',
      green: 'bg-green-500/10 border-green-500/20 hover:bg-green-500/20',
      orange: 'bg-orange-500/10 border-orange-500/20 hover:bg-orange-500/20',
      pink: 'bg-pink-500/10 border-pink-500/20 hover:bg-pink-500/20',
      cyan: 'bg-cyan-500/10 border-cyan-500/20 hover:bg-cyan-500/20',
      yellow: 'bg-yellow-500/10 border-yellow-500/20 hover:bg-yellow-500/20'
    };
    return colors[color] || colors.purple;
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg font-medium">Admin Access (aakashpoudel.com)</h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-sm"
            >
              View Site
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg transition-colors text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats Grid - DYNAMIC */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
          <div className="p-6 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-2">Involvements</div>
            {stats.loading ? (
              <div className="text-2xl font-light text-white/40">...</div>
            ) : (
              <div className="text-3xl font-light">{stats.involvements}</div>
            )}
          </div>
          <div className="p-6 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-2">Awards</div>
            {stats.loading ? (
              <div className="text-2xl font-light text-white/40">...</div>
            ) : (
              <div className="text-3xl font-light">{stats.awards}</div>
            )}
          </div>
          <div className="p-6 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-2">Projects</div>
            {stats.loading ? (
              <div className="text-2xl font-light text-white/40">...</div>
            ) : (
              <div className="text-3xl font-light">{stats.projects}</div>
            )}
          </div>
          <div className="p-6 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-2">Writings</div>
            {stats.loading ? (
              <div className="text-2xl font-light text-white/40">...</div>
            ) : (
              <div className="text-3xl font-light">{stats.writings}</div>
            )}
          </div>
          <div className="p-6 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-2">Research</div>
            {stats.loading ? (
              <div className="text-2xl font-light text-white/40">...</div>
            ) : (
              <div className="text-3xl font-light">{stats.research}</div>
            )}
          </div>
        </div>

        {/* Section Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => {
            return (
              <button
                key={section.route}
                onClick={() => navigate(section.route)}
                className={`p-6 rounded-lg border transition-all text-left ${getColorClasses(section.color)}`}
              >
                <h3 className="text-lg font-medium mb-2">{section.title}</h3>
                {section.stat !== undefined && !stats.loading && (
                  <p className="text-white/40 text-sm">{section.stat} {section.stat === 1 ? 'item' : 'items'}</p>
                )}
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;